import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

const SUGGESTIONS = [
  "What's included in Touchdown Royal?",
  "Best truck for a weekend trip?",
  "How do I book the penthouse?",
];

export const ConciergeChat = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Welcome to Sky Suite Rentals. I'm your concierge — ask me about the fleet, the penthouse, or the Touchdown Royal package.",
    },
  ]);
  const [sessionId] = useState(() => {
    const existing = localStorage.getItem("sky_concierge_session");
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem("sky_concierge_session", id);
    return id;
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const appendDelta = (delta) => {
    setMessages((prev) => {
      const next = [...prev];
      next[next.length - 1] = { role: "assistant", text: next[next.length - 1].text + delta };
      return next;
    });
  };

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((prev) => [...prev, { role: "user", text: message }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") break;
          try {
            const payload = JSON.parse(data);
            if (payload.delta) appendDelta(payload.delta);
            if (payload.error) appendDelta(payload.error);
          } catch {}
        }
      }
    } catch {
      appendDelta("I'm momentarily unavailable — reach us anytime at 1-800-717-7052.");
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="concierge-open-btn"
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="fixed right-6 bottom-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gold text-obsidian shadow-[0_0_35px_rgba(212,175,55,0.45)]"
        aria-label="Open AI concierge"
      >
        {open ? <X size={22} /> : <Sparkles size={22} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="concierge-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-6 bottom-24 z-50 flex h-[480px] w-[calc(100vw-3rem)] max-w-sm flex-col overflow-hidden rounded-2xl border border-gold/25 bg-carbon/95 shadow-2xl backdrop-blur-xl"
          >
            <div className="border-b border-gold/15 px-5 py-4">
              <p className="font-serif text-lg text-slate-100">Sky Concierge</p>
              <p className="font-mono text-[10px] tracking-[0.25em] text-gold uppercase">AI · Sky Suite Rentals</p>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-4" data-testid="concierge-messages">
              {messages.map((m, i) => (
                <div key={i} data-testid={`concierge-message-${i}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "ml-auto bg-gold text-obsidian"
                      : "border border-gold/15 bg-obsidian/60 text-zinc-200"
                  }`}
                >
                  {m.text || (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} data-testid={`concierge-suggestion-${SUGGESTIONS.indexOf(s)}`}
                      onClick={() => send(s)}
                      className="rounded-full border border-gold/25 px-3 py-1.5 text-[11px] text-gold transition-colors hover:bg-gold/10">
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 border-t border-gold/15 p-3">
              <input
                data-testid="concierge-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask the concierge…"
                className="flex-1 rounded-full border border-gold/20 bg-obsidian/80 px-4 py-2.5 text-sm text-slate-100 outline-none placeholder:text-zinc-600 focus:border-gold/60"
              />
              <button
                data-testid="concierge-send-btn"
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-gold text-obsidian transition-opacity disabled:opacity-50"
                aria-label="Send message"
              >
                {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
